'use no memo';

import {useState} from 'react';

import {isEmpty, isNil} from 'ramda';

import {useBoolean} from 'shared/web';

import {useImageSelection} from '../../../hooks/useImageSelection';

// Backend cap per copy/move request; over it the request 400s. Delete is uncapped.
export const MAX_REORGANIZED_PHOTOS = 99;

interface VehicleAlbumBulkActions {
  deleteImagesInBulk: (photoIds: string[]) => Promise<boolean | void>;
  copyImage: (destinationAlbumId: string, photoIds: string | string[]) => Promise<boolean | void>;
  moveImage: (destinationAlbumId: string, photoIds: string | string[]) => Promise<boolean | void>;
}

interface UseVehicleAlbumBulkActionsProps {
  // Photos only, in render order — videos render no checkbox.
  albumPhotoIds: string[];
  actions: VehicleAlbumBulkActions;
  onAlbumChange?: () => void;
  onCopyStarted?: (destinationAlbumId: string, photoCount: number) => void;
}

export function useVehicleAlbumBulkActions(props: UseVehicleAlbumBulkActionsProps) {
  const [isBulkModeActive, enterBulkMode, leaveBulkMode] = useBoolean();
  const [rangeAnchorPhotoId, setRangeAnchorPhotoId] = useState<string | null>(null);
  const selection = useImageSelection();

  // Derived from the current album so a photo removed since it was selected never reaches a
  // bulk request — the delete route rejects the whole batch over one unknown id.
  const selectedPhotoIds = props.albumPhotoIds.filter((photoId) =>
    selection.isImageSelected(photoId)
  );
  const hasSelection = !isEmpty(selectedPhotoIds);

  const resetBulkMode = () => {
    selection.clearImageSelection();
    setRangeAnchorPhotoId(null);
    leaveBulkMode();
  };

  const toggleBulkMode = () => {
    if (isBulkModeActive) {
      resetBulkMode();
    } else {
      enterBulkMode();
    }
  };

  const selectAll = () => selection.selectImagesInBulk(props.albumPhotoIds);

  const handleTileClick = (index: number, isRangeSelect: boolean) => {
    // Anchored by id, not index: the album can refetch between the anchor click and this one.
    const anchorIndex = isNil(rangeAnchorPhotoId)
      ? -1
      : props.albumPhotoIds.indexOf(rangeAnchorPhotoId);

    if (isRangeSelect && anchorIndex !== -1) {
      const start = Math.min(anchorIndex, index);
      const end = Math.max(anchorIndex, index);
      selection.selectImagesInBulk(props.albumPhotoIds.slice(start, end + 1));
      return;
    }

    const photoId = props.albumPhotoIds[index];
    selection.toggleImageSelection(photoId);
    setRangeAnchorPhotoId(photoId);
  };

  const settleAfter = (action: Promise<boolean | void>) =>
    action.then((result) => {
      resetBulkMode();
      if (result) {
        props.onAlbumChange?.();
      }
    });

  const deleteSelected = () => {
    if (!hasSelection) {
      return;
    }
    return settleAfter(props.actions.deleteImagesInBulk(selectedPhotoIds));
  };

  const moveSelectedTo = (destinationAlbumId: string) => {
    if (!hasSelection) {
      return;
    }
    return settleAfter(props.actions.moveImage(destinationAlbumId, selectedPhotoIds));
  };

  const copySelectedTo = (destinationAlbumId: string) => {
    if (!hasSelection) {
      return;
    }
    const photoCount = selectedPhotoIds.length;
    return settleAfter(
      props.actions.copyImage(destinationAlbumId, selectedPhotoIds).then((result) => {
        if (result) {
          props.onCopyStarted?.(destinationAlbumId, photoCount);
        }
        return result;
      })
    );
  };

  return {
    isBulkModeActive,
    toggleBulkMode,
    selectAll,
    handleTileClick,
    isImageSelected: selection.isImageSelected,
    selectedCount: selectedPhotoIds.length,
    hasSelection,
    isOverReorganizeLimit: selectedPhotoIds.length > MAX_REORGANIZED_PHOTOS,
    deleteSelected,
    moveSelectedTo,
    copySelectedTo,
  };
}
